/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import styles from './styles.module.scss';
import dynamic from 'next/dynamic';
import root from 'window-or-global';
import { useEffect, useRef, useState } from 'react';
import { type ProductModel } from '@/api/types/products-types';
import { Actionable } from '@az/starc-ui';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { scrollToCustomerReviews } from '../../utils/scrollToTitle';
import { ReviewStats } from '../ReviewStats/ReviewStats';
import type { ReviewStatistic } from '../../interface/types';
import { roundValueToPrecision } from '@/components/Rating/utils';
import { type ProductAnalyticsData } from '../../interface/types';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

const LazyOverviewPopup = dynamic(() =>
  import('../ReviewsOverview/OverviewPopup/OverviewPopup').then((mod) => mod.OverviewPopup),
);

type ProductStatisticsProps = {
  productDetails: ProductModel | undefined;
  reviewStatistic: ReviewStatistic | undefined;
  reviewsPending?: boolean;
  unsetStyles?: boolean;
};

type ReviewAnalyticsData = {
  rating?: string | number | null;
  reviewCount?: string | number | null;
  productAnalyticsData?: ProductAnalyticsData;
};

const trackReviewLinkPDP = ({ rating, reviewCount, productAnalyticsData }: ReviewAnalyticsData) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data ?? {};

  clickTrack({
    eventType: 'reviewsLinkInteractionPDP',
    customLinkName: `reviewsInteraction - ${pageType}`,
    pageName,
    pageType,
    siteSection,
    productAverageRating: rating,
    productReviewCount: reviewCount,
    ...productAnalyticsData,
  });
};

export const ProductStatistics = ({
  productDetails,
  reviewStatistic,
  reviewsPending,
  unsetStyles,
}: ProductStatisticsProps) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { acesEnabled } = useAcesFlags();
  const anchor = useRef(null);

  const hasReviews = reviewStatistic && Number(reviewStatistic.totalReviewCount) > 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const popper = document.getElementById('product-reviews-popper');
      if (popper && !popper.contains(event.target as Node)) {
        setIsPopupOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClick = () => {
    if (isDesktop) {
      setIsPopupOpen((isOpen) => !isOpen);
      window.dispatchEvent(new CustomEvent('openReviewsAccordion'));
    } else {
      scrollToCustomerReviews();
      window.dispatchEvent(new CustomEvent('openReviewsAccordion'));
    }

    // Prepare analytics data
    const rating = !reviewStatistic
      ? undefined
      : reviewStatistic.averageOverallRating
        ? roundValueToPrecision(Number(reviewStatistic.averageOverallRating), 0.1)
        : '';

    const reviewCount = !reviewStatistic
      ? undefined
      : reviewStatistic.totalReviewCount
        ? Number(reviewStatistic.totalReviewCount)
        : '';

    const productAnalyticsData: ProductAnalyticsData = {
      productIdList: [productDetails?.itemId ?? ''],
      productBrandList: [productDetails?.brandName ?? ''],
      productPartNumberList: productDetails?.partNumber ?? '',
      ...(acesEnabled
        ? {
            partGroupId: [productDetails?.partGroupId ?? ''],
            partTerminologyId: [productDetails?.ecommProductId ?? ''],
          }
        : {
            productRepositoryIdList: productDetails?.ecommProductId ?? '',
            originalPartTypeIdList: productDetails?.originalPartTypeId ?? '',
          }),
    };

    trackReviewLinkPDP({
      rating,
      reviewCount,
      productAnalyticsData,
    });
  };

  return (
    <>
      {isPopupOpen ? (
        <LazyOverviewPopup
          handleClose={() => setIsPopupOpen(false)}
          anchor={anchor}
          reviewStatistic={reviewStatistic}
        />
      ) : null}
      {reviewsPending ? <div className={unsetStyles ? '' : styles.statisticsContainer} /> : null}
      {!reviewsPending && hasReviews ? (
        <div className={unsetStyles ? '' : styles.statisticsContainer}>
          <Actionable onClick={handleClick} ref={anchor}>
            <ReviewStats reviewStatistic={reviewStatistic} />
          </Actionable>
        </div>
      ) : null}
    </>
  );
};
